import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import type {
  BuildingFilters,
  BuildingListItem,
  BuildingListResponse,
} from '../types';

/**
 * `useBuildings` — TanStack Query hook over Core's `GET /api/v1/buildings`
 * via the apps/api proxy. Subtree scoping is automatic: the apps/api proxy
 * (W2-F2 middleware) injects the `X-Partner-Subtree` header derived from
 * the current user's tenant context, so Senviro doesn't need to send any
 * partner-scoping headers itself.
 *
 * Filters that Core supports natively (`status`, `building_type_id`) are
 * forwarded via query string. `partnerOrgId` and `search` are tracked
 * client-side so the UI can chip-filter without a refetch — the table
 * filters the cached list in-place. We pass `partnerOrgId` into the query
 * key so saved filters from URL params produce a stable cache.
 *
 * 5-min `staleTime` matches the global default (`lib/query-client.ts`).
 */
export function useBuildings(
  filters: BuildingFilters = {},
): UseQueryResult<BuildingListItem[]> {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ['buildings', normalizeFilters(filters)] as const,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.propertyType)
        params.set('building_type_id', filters.propertyType);
      const qs = params.toString();
      const path = `/api/v1/proxy/core/buildings${qs ? `?${qs}` : ''}`;
      const result = await apiFetch<BuildingListResponse | BuildingListItem[]>(
        path,
        { getAccessToken },
      );
      // Tolerate both list-response envelope and bare-array shapes; Core
      // ADR 0024 returns `ListResponse_BuildingRead_` for /buildings but
      // the apps/api proxy strips the outer `{ data }` envelope.
      return Array.isArray(result) ? result : result.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

function normalizeFilters(filters: BuildingFilters): BuildingFilters {
  // Sort + drop empty values to keep the query key stable across reorderings
  // of the URL params.
  const out: BuildingFilters = {};
  if (filters.status) out.status = filters.status;
  if (filters.propertyType) out.propertyType = filters.propertyType;
  if (filters.partnerOrgId) out.partnerOrgId = filters.partnerOrgId;
  if (filters.search) out.search = filters.search;
  return out;
}

/**
 * Sort keys accepted by the W76-F4-S1 paginated buildings endpoint. Must stay
 * in sync with the API whitelist (`apps/api/src/proxy-core/buildings-pagination.ts`).
 */
export type BuildingsSort =
  | 'name'
  | '-name'
  | 'updated_at'
  | '-updated_at'
  | 'gross_floor_area_m2'
  | '-gross_floor_area_m2';

/** Default page size used by the infinite hook (matches the API default). */
export const BUILDINGS_PAGE_SIZE = 50;

/** Cursor-paginated envelope returned when any filter param is active. */
export interface BuildingsInfinitePage {
  data: BuildingListItem[];
  nextCursor: string | null;
  prevCursor: string | null;
  total?: number;
}

export interface BuildingsInfiniteFilters extends BuildingFilters {
  /** Sort key — defaults to `-updated_at` server-side when omitted. */
  sort?: BuildingsSort;
  /** Page size (capped at 200 server-side). Defaults to 50. */
  limit?: number;
}

/**
 * `useBuildingsInfinite` — W76-F4-S1 sibling to {@link useBuildings}. Consumes
 * the cursor-paginated envelope from `GET /api/v1/proxy/core/buildings` when
 * any of `q`/`sort`/`cursor`/`limit` is sent. The list page swaps to this hook
 * when a filter is active; the unbounded {@link useBuildings} stays the default
 * (and is what the Map page keeps using).
 *
 * The endpoint is backwards-compatible: this hook always sends `limit`, so it
 * always receives the new `{ data, nextCursor, prevCursor, total? }` shape.
 */
export function useBuildingsInfinite(
  filters: BuildingsInfiniteFilters = {},
): UseInfiniteQueryResult<{
  pages: BuildingsInfinitePage[];
  pageParams: unknown[];
}> {
  const { getAccessToken } = useAuth();
  const limit = filters.limit ?? BUILDINGS_PAGE_SIZE;

  return useInfiniteQuery<BuildingsInfinitePage>({
    queryKey: [
      'buildings-infinite',
      normalizeFilters(filters),
      filters.sort ?? null,
      limit,
    ] as const,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.propertyType)
        params.set('building_type_id', filters.propertyType);
      if (filters.search) params.set('q', filters.search);
      if (filters.sort) params.set('sort', filters.sort);
      params.set('limit', String(limit));
      if (pageParam) params.set('cursor', pageParam as string);
      const path = `/api/v1/proxy/core/buildings?${params.toString()}`;
      return apiFetch<BuildingsInfinitePage>(path, { getAccessToken });
    },
    staleTime: 5 * 60 * 1000,
  });
}
